import * as turf from '@turf/turf';
import cloneDeep from 'lodash/cloneDeep';
import flow from 'lodash/fp/flow';
import map from 'lodash/fp/map';
import thru from 'lodash/fp/thru';
import uniqBy from 'lodash/fp/uniqBy';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import reduce from 'lodash/reduce';
import set from 'lodash/set';
import setWith from 'lodash/setWith';
import moment from 'moment';

import {FORM_FIELDS} from '@components/Person/PersonOptionsSelection/MapPeopleFilter/PersonLocationTypesSelector/constants';
import {SITE_COUNT_AS_VALUES} from '@components/world-map-box/_shared/filter/MapMenuDrawer/MapFilterDrawer/SiteTab/constants';

import {FEATURES} from '@constants/features';
import {MAP_SECURITY_TAB_FORM} from '@constants/map-security';
import {LAYERS} from '@constants/mapbox/mapbox';

import {featureToggle} from '@utils/featureToggle/featureToggle';
import horizonJSON from '@utils/json';

import mapboxUtils, {MAP_STYLES} from '../../../components/Common/Mapbox/utils/mapbox-utils';
import {
  AIRLINE_ID,
  AIRPORT_ID,
  FLIGHT_NUMBER_LIKE,
  INCLUDE_SUB_ORGS,
  PERSON_ORGS,
  SEPARATE_MOBILE_LOCATIONS,
} from '../../../components/Person/PersonOptionsSelection/form-constants';
import {
  ALERT_ACKNOWLEDGEMENT_ORGANIZATION_ID,
  ALERT_ACKNOWLEDGEMENT_STATUSES,
  ALERT_CATEGORIES,
  ALERT_PROVIDERS,
  ALERT_REQUIRE_EXPOSURE,
  ALERT_SEARCH_MODE,
  ALERT_SEARCH_TERMS,
  ALERT_SEVERITIES,
  SITE_REQUIRE_EXPOSURE,
  WORLD_MAP_FILTER_STORAGE_KEY_BASE,
} from '../../../constants/alert-filters';
import {CATEGORY_IDS} from '../../../constants/categories';
import {MAP_LOCATIONS_FILTER} from '../../../constants/map';
import {
  COUNT_AS_FILTER,
  SITE_FILTER,
  SITE_TYPES_FILTER,
  WORLD_MAP_FILTER_SITES_STORAGE_KEY_BASE,
} from '../../../constants/map-sites';
import {WORLD_MAP_FILTER_PERSON_STORAGE_KEY_BASE} from '../../../constants/person-filters';
import {
  MAP_RATINGS,
  MAP_RATINGS_CATEGORY,
  MAP_RATINGS_DEFAULT_RANGE,
  MAP_RATINGS_LEVELS,
  MAP_RATINGS_ORGS,
  THREATZONE_DEFAULT_RANGE,
  WORLD_MAP_RATINGS_FILTER_STORAGE_KEY_BASE,
} from '../../../constants/rating-filters';
import {
  THREAT_ZONE_LEVELS,
  THREAT_ZONE_ORGS,
  THREAT_ZONES,
  WORLD_MAP_FILTER_TZ_STORAGE_KEY_BASE,
} from '../../../constants/threat-zones-filters';
import {horizonLocalStorage} from '../../../utils/local-storage';
import {getRatingKey} from '../../../utils/rating-filters';
import {getCategoryName} from '../categories/utils';
import {constants} from '../persons/utils';
import {getSeverityMappinsValues, isAlertFeature, isAllAlertLayersTurnedOn} from './alert/utils';
import {getOrgsWithViewPersonsPermission} from './person/utils';
import {formRatingObjectFromFeatureRatings, isProvinceRatingFeature, isRatingFeature} from './rating/utils';
import * as types from './types';

export const MAX_ZOOM = 16;

const WORLD_MAP_STORAGE_KEY = 'world-map-state';

export const STATE_PROP = {
  DETAIL_FEATURE: 'detailFeature',
  PROVINCE_FEATURE: 'provinceFeature',
};

export const DEFAULT_FILTER_VALUES = {
  layerVisibility: {
    alerts: true,
    countryRatings: false,
    countryThreatZone: false,
    provinceThreatZone: false,
    cityThreatZone: false,
    person: false,
    crisisSignal: false,
    sites: false,
    [MAP_SECURITY_TAB_FORM.ALARMS]: false,
    [MAP_SECURITY_TAB_FORM.CAMERAS]: false,
  },
  languageUnsupportedWarningShown: false,
  locationsFilter: undefined,
  alertsFilter: {
    severities: getSeverityMappinsValues(),
    categories: [],
    [ALERT_SEARCH_TERMS]: [],
    [ALERT_SEARCH_MODE]: undefined,
    [ALERT_ACKNOWLEDGEMENT_STATUSES]: [],
    [ALERT_ACKNOWLEDGEMENT_ORGANIZATION_ID]: undefined,
    [ALERT_PROVIDERS]: [],
  },
  ratingsFilter: {
    [MAP_RATINGS]: [],
    [MAP_RATINGS_CATEGORY]: CATEGORY_IDS.OVERALL,
    [MAP_RATINGS_LEVELS]: MAP_RATINGS_DEFAULT_RANGE,
  },
  threatZoneFilter: {
    [THREAT_ZONES]: [],
    [THREAT_ZONE_LEVELS]: THREATZONE_DEFAULT_RANGE,
  },
  personFilter: {
    [FORM_FIELDS.PERSON_LOCATION_TYPES]: undefined,
    [FORM_FIELDS.MOBILE_LOCATION_SCOPE]: undefined,
    [PERSON_ORGS]: [],
    includeSubOrganizations: true,
  },
  siteFilter: {
    [SITE_FILTER]: false,
    [COUNT_AS_FILTER]: SITE_COUNT_AS_VALUES.SITES,
    [SITE_TYPES_FILTER]: undefined,
  },
  mapType: MAP_STYLES.STREET.id,
};

export const MAX_NONEXTENDED_BOUNDS_ZOOM = 4;
export const MAX_EXTENDED_BOUNDS = turf.envelope(
  turf.multiPoint([
    [-180, -90],
    [180, 90],
  ]),
);

/**
 * Return the mapType stored in LocalStorage.
 * If no style is stored, or if the style stored is invalid, return 'street'.
 * @param worldMapLocalStorage
 * @return {string}
 */
export function getMapTypeFromLocalStore(worldMapLocalStorage) {
  let mapType = get(worldMapLocalStorage, 'mapType', MAP_STYLES.STREET.id);

  if (!mapboxUtils.getMapStyles().includes(mapType)) {
    //maptype is invalid, default to street
    mapType = MAP_STYLES.STREET.id;
  }

  return mapType;
}

/**
 * Returns the local storage object for 'world-map-state' key
 *
 * @returns {*}
 */
export function getWorldMapLocalStorage() {
  let worldMapLocalStorage = horizonJSON.parse(horizonLocalStorage.getItem(WORLD_MAP_STORAGE_KEY)) ?? {};

  const readLayerVisibility = (layerKey) =>
    get(worldMapLocalStorage, `layerVisibility.${layerKey}`, DEFAULT_FILTER_VALUES.layerVisibility[layerKey]);

  return {
    layerVisibility: {
      alerts: readLayerVisibility('alerts'),
      countryRatings: readLayerVisibility('countryRatings'),
      provinceRatings: readLayerVisibility('provinceRatings'),
      cityRatings: readLayerVisibility('cityRatings'),
      countryThreatZone: readLayerVisibility('countryThreatZone'),
      provinceThreatZone: readLayerVisibility('provinceThreatZone'),
      cityThreatZone: readLayerVisibility('cityThreatZone'),
      person: readLayerVisibility('person'),
      crisisSignal: readLayerVisibility('crisisSignal'),
      sites: readLayerVisibility('sites'),
      [MAP_SECURITY_TAB_FORM.ALARMS]: readLayerVisibility(MAP_SECURITY_TAB_FORM.ALARMS),
      [MAP_SECURITY_TAB_FORM.CAMERAS]: readLayerVisibility(MAP_SECURITY_TAB_FORM.CAMERAS),
    },
    languageUnsupportedWarningShown: get(
      worldMapLocalStorage,
      'languageUnsupportedWarningShown',
      DEFAULT_FILTER_VALUES.languageUnsupportedWarningShown,
    ),
    locationsFilter: get(worldMapLocalStorage, [MAP_LOCATIONS_FILTER], DEFAULT_FILTER_VALUES.locationsFilter),
    alertsFilter: {
      severities: get(
        worldMapLocalStorage,
        [WORLD_MAP_FILTER_STORAGE_KEY_BASE, ALERT_SEVERITIES],
        DEFAULT_FILTER_VALUES.alertsFilter.severities,
      ),
      categories: get(
        worldMapLocalStorage,
        [WORLD_MAP_FILTER_STORAGE_KEY_BASE, ALERT_CATEGORIES],
        DEFAULT_FILTER_VALUES.alertsFilter.categories,
      ),
      [ALERT_REQUIRE_EXPOSURE]: get(
        worldMapLocalStorage,
        [WORLD_MAP_FILTER_STORAGE_KEY_BASE, ALERT_REQUIRE_EXPOSURE],
        false,
      ),
      [SITE_REQUIRE_EXPOSURE]: get(
        worldMapLocalStorage,
        [WORLD_MAP_FILTER_STORAGE_KEY_BASE, SITE_REQUIRE_EXPOSURE],
        false,
      ),
      [ALERT_SEARCH_TERMS]: get(
        worldMapLocalStorage,
        [WORLD_MAP_FILTER_STORAGE_KEY_BASE, ALERT_SEARCH_TERMS],
        DEFAULT_FILTER_VALUES.alertsFilter[ALERT_SEARCH_TERMS],
      ),
      [ALERT_SEARCH_MODE]: get(
        worldMapLocalStorage,
        [WORLD_MAP_FILTER_STORAGE_KEY_BASE, ALERT_SEARCH_MODE],
        DEFAULT_FILTER_VALUES.alertsFilter[ALERT_SEARCH_MODE],
      ),
      [ALERT_ACKNOWLEDGEMENT_STATUSES]: get(
        worldMapLocalStorage,
        [WORLD_MAP_FILTER_STORAGE_KEY_BASE, ALERT_ACKNOWLEDGEMENT_STATUSES],
        DEFAULT_FILTER_VALUES.alertsFilter[ALERT_ACKNOWLEDGEMENT_STATUSES],
      ),
      [ALERT_ACKNOWLEDGEMENT_ORGANIZATION_ID]: get(
        worldMapLocalStorage,
        [WORLD_MAP_FILTER_STORAGE_KEY_BASE, ALERT_ACKNOWLEDGEMENT_ORGANIZATION_ID],
        DEFAULT_FILTER_VALUES.alertsFilter[ALERT_ACKNOWLEDGEMENT_ORGANIZATION_ID],
      ),
      [ALERT_PROVIDERS]: get(
        worldMapLocalStorage,
        [WORLD_MAP_FILTER_STORAGE_KEY_BASE, ALERT_PROVIDERS],
        DEFAULT_FILTER_VALUES.alertsFilter[ALERT_PROVIDERS],
      ),
    },
    ratingsFilter: {
      [MAP_RATINGS]: get(
        worldMapLocalStorage,
        [WORLD_MAP_RATINGS_FILTER_STORAGE_KEY_BASE, MAP_RATINGS],
        DEFAULT_FILTER_VALUES.ratingsFilter[MAP_RATINGS],
      ),
      [MAP_RATINGS_CATEGORY]: get(
        worldMapLocalStorage,
        [WORLD_MAP_RATINGS_FILTER_STORAGE_KEY_BASE, MAP_RATINGS_CATEGORY],
        DEFAULT_FILTER_VALUES.ratingsFilter[MAP_RATINGS_CATEGORY],
      ),
      [MAP_RATINGS_LEVELS]: get(
        worldMapLocalStorage,
        [WORLD_MAP_RATINGS_FILTER_STORAGE_KEY_BASE, MAP_RATINGS_LEVELS],
        DEFAULT_FILTER_VALUES.ratingsFilter[MAP_RATINGS_LEVELS],
      ),
      [MAP_RATINGS_ORGS]: get(worldMapLocalStorage, [WORLD_MAP_RATINGS_FILTER_STORAGE_KEY_BASE, MAP_RATINGS_ORGS]),
    },
    threatZoneFilter: {
      [THREAT_ZONES]: get(worldMapLocalStorage, [WORLD_MAP_FILTER_TZ_STORAGE_KEY_BASE, THREAT_ZONES], []),
      [THREAT_ZONE_LEVELS]: get(
        worldMapLocalStorage,
        [WORLD_MAP_FILTER_TZ_STORAGE_KEY_BASE, THREAT_ZONE_LEVELS],
        DEFAULT_FILTER_VALUES.threatZoneFilter[THREAT_ZONE_LEVELS],
      ),
      [THREAT_ZONE_ORGS]: get(worldMapLocalStorage, [WORLD_MAP_FILTER_TZ_STORAGE_KEY_BASE, THREAT_ZONE_ORGS]),
    },
    personFilter: {
      [FORM_FIELDS.PERSON_LOCATION_TYPES]: get(
        worldMapLocalStorage,
        [WORLD_MAP_FILTER_PERSON_STORAGE_KEY_BASE, FORM_FIELDS.PERSON_LOCATION_TYPES],
        DEFAULT_FILTER_VALUES.personFilter[FORM_FIELDS.PERSON_LOCATION_TYPES],
      ),
      [FORM_FIELDS.MOBILE_LOCATION_SCOPE]: get(
        worldMapLocalStorage,
        [WORLD_MAP_FILTER_PERSON_STORAGE_KEY_BASE, FORM_FIELDS.MOBILE_LOCATION_SCOPE],
        DEFAULT_FILTER_VALUES.personFilter[FORM_FIELDS.MOBILE_LOCATION_SCOPE],
      ),
      [PERSON_ORGS]: get(
        worldMapLocalStorage,
        [WORLD_MAP_FILTER_PERSON_STORAGE_KEY_BASE, PERSON_ORGS],
        DEFAULT_FILTER_VALUES.personFilter[PERSON_ORGS],
      ),
      [INCLUDE_SUB_ORGS]: get(
        worldMapLocalStorage,
        [WORLD_MAP_FILTER_PERSON_STORAGE_KEY_BASE, INCLUDE_SUB_ORGS],
        DEFAULT_FILTER_VALUES.personFilter[INCLUDE_SUB_ORGS],
      ),
      [SEPARATE_MOBILE_LOCATIONS]: get(
        worldMapLocalStorage,
        [WORLD_MAP_FILTER_PERSON_STORAGE_KEY_BASE, SEPARATE_MOBILE_LOCATIONS],
        DEFAULT_FILTER_VALUES.personFilter[SEPARATE_MOBILE_LOCATIONS],
      ),
    },
    [WORLD_MAP_FILTER_SITES_STORAGE_KEY_BASE]: {
      [SITE_FILTER]: get(
        worldMapLocalStorage,
        [WORLD_MAP_FILTER_SITES_STORAGE_KEY_BASE, SITE_FILTER],
        DEFAULT_FILTER_VALUES.siteFilter[SITE_FILTER],
      ),
      [COUNT_AS_FILTER]: get(
        worldMapLocalStorage,
        [WORLD_MAP_FILTER_SITES_STORAGE_KEY_BASE, COUNT_AS_FILTER],
        DEFAULT_FILTER_VALUES.siteFilter[COUNT_AS_FILTER],
      ),
      [SITE_TYPES_FILTER]: get(
        worldMapLocalStorage,
        [WORLD_MAP_FILTER_SITES_STORAGE_KEY_BASE, SITE_TYPES_FILTER],
        DEFAULT_FILTER_VALUES.siteFilter[SITE_TYPES_FILTER],
      ),
    },
    mapType: getMapTypeFromLocalStore(worldMapLocalStorage),
  };
}

/**
 * Compares the current local storage object for 'world-map-state' key to the default state.
 *
 * @param storageObject The current state of local storage to compare to the default state.
 * @returns boolean
 */
export const isDefaultLayerPermissions = (storageObject) => isEqual(DEFAULT_FILTER_VALUES, storageObject);

export function isTimeFilterOn(worldMapState) {
  return (
    moment(worldMapState?.beginDateTime).diff(getDefaultStartTime(), 'minutes') > 5 ||
    moment(worldMapState?.endDateTime).diff(getDefaultEndTime(), 'minutes') > 5
  );
}

export function isPersonAdvanceSearchActive(worldMapState) {
  const personFilter = worldMapState?.personFilter;
  const isActive =
    personFilter?.[AIRLINE_ID]?.value ||
    personFilter?.[AIRPORT_ID]?.value ||
    personFilter?.[FLIGHT_NUMBER_LIKE] ||
    personFilter?.[constants.ADDRESS_ACCURACY]?.length > 0 ||
    personFilter?.[constants.ADDRESS_LABELS]?.length > 0;

  return isActive;
}

const sortRatingLevels = (values) => {
  return [...(values || [])].sort((a, b) => {
    if (a === b) {
      return 0;
    }

    if (a === undefined) {
      return 1;
    }

    if (b === undefined) {
      return -1;
    }

    return a - b;
  });
};

export const isRatingLevelsEqualDefault = (ratingLevels) => {
  return isEqual(sortRatingLevels(ratingLevels), sortRatingLevels(MAP_RATINGS_DEFAULT_RANGE));
};

export const hasFilterAppliedToLayers = (mapFilters, getState) => {
  const permissionMap = get(getState(), ['session', 'currentPermissions', 'permissions'], {});

  const worldMapState = get(getState(), 'worldMap');
  const hasLocationsFilter = !isEmpty(mapFilters?.locationsFilter);

  const alertFilter = mapFilters?.alertsFilter;
  // if alerts is active
  if (mapFilters.layerVisibility?.alerts && !isEmpty(alertFilter?.severities)) {
    // if time range is different than default
    if (
      hasLocationsFilter ||
      isTimeFilterOn(worldMapState) ||
      worldMapState?.includeExpiredIntelligence ||
      !isEmpty(alertFilter?.categories) ||
      !isAllAlertLayersTurnedOn(permissionMap, alertFilter)
    ) {
      return true;
    }
  }

  const ratingFilter = mapFilters?.ratingsFilter;
  if (!isEmpty(ratingFilter?.mapRatings)) {
    // rating layer is on (country/province/city and a filter is applied)
    if (
      hasLocationsFilter ||
      ratingFilter?.[MAP_RATINGS_CATEGORY] !== CATEGORY_IDS.OVERALL ||
      !isRatingLevelsEqualDefault(ratingFilter?.[MAP_RATINGS_LEVELS])
    ) {
      return true;
    }
  }

  if (mapFilters.layerVisibility?.person) {
    // if time range is different than default
    if (hasLocationsFilter || isTimeFilterOn(worldMapState) || isPersonAdvanceSearchActive(worldMapState)) {
      return true;
    }
  }

  return false;
};

/**
 * Sets the local storage object for 'world-map-state' key
 *
 * @returns {*}
 */
export function setWorldMapLocalStorage(storageObject) {
  horizonLocalStorage.setItem(WORLD_MAP_STORAGE_KEY, JSON.stringify(storageObject));
}

/**
 * Returns true if lat / long are inside the rectangle defined by
 * southwest and northeast. southwest in inclusive, northeast is not.
 *
 * @param long - longitude number
 * @param lat - latitude number
 * @param southwest - lng,lat array
 * @param northeast - lng,lat array
 * @returns {boolean}
 */
export function isPointInRectangle(long, lat, southwest, northeast) {
  return southwest[0] <= long && long < northeast[0] && southwest[1] <= lat && lat < northeast[1];
}

export function expandClusteredAlertChildren(feature) {
  return flow([
    map((child) => ({
      ...feature,
      ...child,
      bounds: feature.bounds, // keep bounds of entire cluster
    })),
    uniqBy('properties.id'),
    thru((items) => items.sort((a, b) => b.properties?.severityKey?.localeCompare(a.properties?.severityKey))),
  ])(feature.children);
}

/**
 * Expands features to break out their cluster children when necessary and
 * removes duplication of incident feature items if exists
 *
 * @param features
 * @param getState to get redux state
 * @returns {*}
 */
export function transformFeatures(features, getState, ratingsFilters) {
  return reduce(
    features,
    (reducer, feature) => {
      const isClustered = get(feature, 'properties.cluster');
      const isAlert = isAlertFeature(feature);
      const isRating = isRatingFeature(feature);

      // Pull out alert clustered children and add individually
      if (isAlert && isClustered) {
        let feats;
        if (featureToggle(FEATURES.HZFE_258)) {
          feats = feature;
        } else {
          // Expanded Children
          feats = expandClusteredAlertChildren(feature);
        }

        return reducer.concat(feats);
      }

      // Map the stringified categories to array
      if (isAlert && !isClustered) {
        feature.properties.categories = horizonJSON.parse(feature.properties.categories);

        return reducer.concat(feature);
      }

      if (isRating) {
        let clonedFeature = cloneDeep(feature);

        const locale = get(getState(), ['localization', 'locale']);
        const currentCategoryId = ratingsFilters
          ? ratingsFilters[MAP_RATINGS_CATEGORY]
          : get(getState(), ['worldMap', WORLD_MAP_RATINGS_FILTER_STORAGE_KEY_BASE, MAP_RATINGS_CATEGORY]);
        const ratingsDict = get(getState(), 'categories.dict', {});
        const ratingKey = getRatingKey(currentCategoryId);
        clonedFeature.currentCategoryRating = get(clonedFeature, ['properties', ratingKey]);
        clonedFeature.currentCategoryName = getCategoryName(ratingsDict, currentCategoryId);

        clonedFeature.currentCategoryId = currentCategoryId;
        clonedFeature.ratingsDict = ratingsDict;

        clonedFeature.detailsRatings = formRatingObjectFromFeatureRatings(feature, ratingsDict, locale);

        if (
          clonedFeature.layer.id === LAYERS.RATINGS_CITY_LAYER_BOUNDARY_ID ||
          clonedFeature.layer.id === LAYERS.RATINGS_CITY_LAYER_POINT_ID
        ) {
          const currentLanguage = get(getState(), 'localization.language');
          clonedFeature.properties = {
            ...clonedFeature.properties,
            locationId: clonedFeature.properties.id,
            locationName: clonedFeature.properties[`locationName_${currentLanguage}`],
            locationTier: clonedFeature.properties[`locationTierName_${currentLanguage}`],
            parentLocationName:
              clonedFeature.properties[`parentLocationName_${currentLanguage}`] ||
              clonedFeature.properties['parentLocationName_en'],
          };
        }

        return reducer.concat(clonedFeature);
      }

      // Return everything else normally
      return reducer.concat(feature);
    },
    [],
  );
}

export function getMapSelectedPeopleOrgs(state) {
  const personOrgsFilter = get(state, ['worldMap', WORLD_MAP_FILTER_PERSON_STORAGE_KEY_BASE, PERSON_ORGS], []);
  const fullPermissions = get(state, ['session', 'fullPermissions'], []);
  const defaultOrgsFilter = getOrgsWithViewPersonsPermission(fullPermissions);
  return personOrgsFilter?.length > 0 ? personOrgsFilter : defaultOrgsFilter;
}

export function getCurrentOrgs(state) {
  return getMapSelectedPeopleOrgs(state);
}

/**
 * Returns global state current worldMap.provinceFeature feature if isProvinceFeature else returns
 * worldMap.detailFeature
 * @param getState
 * @param isProvinceFeature
 * @returns {undefined}
 */
export function getStateCurrentFeature(getState, isProvinceFeature) {
  return isProvinceFeature
    ? cloneDeep(get(getState(), 'worldMap.provinceFeature'))
    : cloneDeep(get(getState(), 'worldMap.detailFeature'));
}

/**
 * Pulls needed props from previousFeature
 * @param previousFeature
 * @returns {{featureCollection: undefined, countryId: undefined}}
 */
export function getPreviousFeatureProps(previousFeature) {
  const featureCollection = get(previousFeature, ['featureCollection']);
  const countryId = get(previousFeature, ['properties', 'countryId']);

  return {
    featureCollection,
    countryId,
  };
}

/**
 * Base on feature layer ID return dispatch object for ProvinceFeature if province layer
 * else dispatch object for detailFeature
 *
 * @param detailFeature
 * @returns {{payload: {}, type: (string)}}
 */
export function formProvinceOrDetailDispatchObject(detailFeature) {
  const isProvinceFeature = isProvinceRatingFeature(detailFeature);
  const type = isProvinceFeature ? types.SET_MAP_PROVINCE_FEATURE : types.SET_MAP_DETAIL_FEATURE;
  const payload = {[isProvinceFeature ? STATE_PROP.PROVINCE_FEATURE : STATE_PROP.DETAIL_FEATURE]: detailFeature};

  return {type, payload};
}

/**
 * Interface to update local storage for 'world-map-state' key
 *
 * @param path The path (used by Lodash) to the element in the store
 * @param value The value to set that element.
 * @returns undefined
 */
export const updateLocalStorage = (path, value) => {
  setWorldMapLocalStorage(set(getWorldMapLocalStorage(), path, value));
};

// this is needed since the function updateLocalStorage
// does not allow to update storage with undefined value
export const updateLocalStorageWithUndefined = (path, value) => {
  const obj = getWorldMapLocalStorage();
  setWith(obj, path, value, Object);
  setWorldMapLocalStorage(obj);
};

export function getDefaultStartTime() {
  return moment().utc().startOf('minutes').subtract(1, 'days');
}

export function getDefaultEndTime() {
  return moment().utc().startOf('minutes');
}

export function extendedBoundNeedsUpdating(zoom, bounds, worldMap) {
  const currentZoom = get(worldMap, ['zoom']);
  const currentExtendedBounds = get(
    worldMap,
    ['extendedBounds'],
    turf.envelope(
      turf.multiPoint([
        [0, 0],
        [0, 0],
      ]),
    ),
  );
  const boundsEnvelope = turf.envelope(turf.multiPoint(bounds));

  // if current extend bounds contains current bounds, OR zoom(int) has changed
  return !turf.booleanContains(currentExtendedBounds, boundsEnvelope) || parseInt(currentZoom) !== parseInt(zoom);
}

export function calculateExtendedBounds(zoom, bounds) {
  if (zoom > MAX_NONEXTENDED_BOUNDS_ZOOM) {
    const lngDistance = bounds[1][0] - bounds[0][0];
    const latDistance = bounds[1][1] - bounds[0][1];

    return turf.envelope(
      turf.multiPoint([
        [bounds[0][0] - lngDistance, Math.max(bounds[0][1] - latDistance, -90)],
        [bounds[1][0] + lngDistance, Math.min(bounds[1][1] + latDistance, 90)],
      ]),
    );
  }

  //else
  return MAX_EXTENDED_BOUNDS;
}

export function getExtendedBoundsIfNecessary(zoom, bounds, worldMap) {
  if (extendedBoundNeedsUpdating(zoom, bounds, worldMap)) {
    return calculateExtendedBounds(zoom, bounds);
  }
}

export function getDefaultDateRange() {
  const beginDateTime = moment().utc().add(5, 'minutes').startOf('minutes').subtract(1, 'days');
  const endDateTime = moment().utc().add(5, 'minutes').startOf('minutes');

  return {
    beginDateTime,
    endDateTime,
  };
}

export function getPermissionsObjectFromOrgId(orgId, state) {
  const ratingPermissions = state?.session?.organizationsDictionary?.[orgId]?.permissions || {};
  return Object.keys(ratingPermissions).reduce((res, item) => ({...res, [item]: item}), {});
}

export function getPermissionsObjectFromOrgIds(orgIds, state) {
  return orgIds.reduce((res, orgId) => {
    const perm = getPermissionsObjectFromOrgId(orgId, state);
    res = {...res, ...perm};

    return res;
  }, {});
}

export * from './alert/utils';
export * from './person/utils';
export * from './rating/utils';
export * from './threat-zone/utils';
